import { CustomEnduserField, CustomFields, DatabaseSelectResponse, TableInputCell } from "@tellescope/types-models";
import { Checkbox, DatePicker, Input, MultiSelect, StringSelect, useDateFnsLocale } from "@tellescope/ui";
import { DatabaseSelectField, InputTableField } from "@tellescope/forms";
import { useCallback, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns/format";
import { ReadOnlyField } from "./ReadOnlyField";
import { i18n } from "@tellescope/i18n";

export type CustomFieldValue = Exclude<CustomFields[string], undefined>;

export interface CustomFieldInputProps {
  customField: CustomEnduserField;
  value: CustomFieldValue | undefined;
  onChange: (next: CustomFieldValue) => void;
  userId?: string;
  id?: string;
  readOnly?: boolean;
}

const parseDateString = (raw: string): Date | undefined => {
  if (!raw) return undefined;
  const mmddyyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw);
  if (mmddyyyy) {
    const [, m, d, y] = mmddyyyy;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (!Number.isNaN(date.getTime())) return date;
  }
  const iso = new Date(raw);
  return Number.isNaN(iso.getTime()) ? undefined : iso;
};

const toStringArray = (value: CustomFieldValue | undefined): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
};

const isTableValue = (value: CustomFieldValue | undefined): value is TableInputCell[][] => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (row) =>
      Array.isArray(row) &&
      row.every(
        (cell) =>
          cell !== null &&
          typeof cell === "object" &&
          "label" in cell &&
          "entry" in cell &&
          typeof cell.label === "string" &&
          typeof cell.entry === "string"
      )
  );
};

const isDatabaseSelectValue = (value: CustomFieldValue | undefined): value is DatabaseSelectResponse[] => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (item) => item !== null && typeof item === "object" && "text" in item && typeof item.text === "string"
  );
};

const getReadOnlyDisplay = (
  customField: CustomEnduserField,
  value: CustomFieldValue | undefined,
  locale: NonNullable<Parameters<typeof format>[2]>["locale"]
): string => {
  switch (customField.type) {
    case "Number":
      return typeof value === "number" ? String(value) : typeof value === "string" ? value : "";
    case "Date": {
      const parsed = parseDateString(typeof value === "string" ? value : "");
      return parsed ? format(parsed, "MM/dd/yyyy", { locale }) : "";
    }
    case "Multiple Select":
    case "Multiple Text":
      return toStringArray(value).join(", ");
    case "Checkbox":
      return typeof value === "string" && value.startsWith("checkbox:") ? i18n.t("Yes") : i18n.t("No");
    case "Database Select":
      return (isDatabaseSelectValue(value) ? value : []).map((item) => item.text).join(", ");
    case "Table": {
      const rows = isTableValue(value) ? value : [];
      return rows.map((row) => row.map((cell) => `${cell.label}: ${cell.entry}`).join(", ")).join("\n");
    }
    default:
      return typeof value === "string" ? value : "";
  }
};

export const CustomFieldInput = ({ customField, value, onChange, userId, id, readOnly }: CustomFieldInputProps) => {
  const { t } = useTranslation("contacts");
  const locale = useDateFnsLocale();
  const generatedId = useId();
  const datalistId = useId();
  const fieldId = id ?? generatedId;

  const stringValue = typeof value === "string" ? value : "";

  const stringOptions = useMemo(() => {
    if (customField.type === "Select" || customField.type === "Multiple Select") {
      return (customField.info.options ?? []).map((o) => ({ label: o, value: o }));
    }
    return [];
  }, [customField]);

  const stringArrayToMultiSelectOptions = useCallback(
    (current: string[]) => current.map((v) => ({ label: v, value: v })),
    []
  );

  if (readOnly) {
    return (
      <ReadOnlyField
        id={fieldId}
        value={getReadOnlyDisplay(customField, value, locale)}
        placeholder={t("Not provided")}
      />
    );
  }

  switch (customField.type) {
    case "Text": {
      return <Input id={fieldId} value={stringValue} onChange={(e) => onChange(e.target.value)} />;
    }

    case "Number": {
      const numString = typeof value === "number" ? String(value) : typeof value === "string" ? value : "";
      return (
        <Input
          id={fieldId}
          type="number"
          value={numString}
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange("");
              return;
            }
            const parsed = Number(raw);
            onChange(Number.isNaN(parsed) ? raw : parsed);
          }}
        />
      );
    }

    case "Date": {
      return (
        <DatePicker
          id={fieldId}
          value={parseDateString(stringValue)}
          onChange={(d) => onChange(d ? format(d, "MM-DD-YYYY", { locale: undefined }) : "")}
          className="space-y-0"
          label=""
        />
      );
    }

    case "Select": {
      if (customField.info?.other) {
        return (
          <>
            <Input id={fieldId} value={stringValue} onChange={(e) => onChange(e.target.value)} list={datalistId} />
            <datalist id={datalistId}>
              {(customField.info.options ?? []).map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </>
        );
      }
      return (
        <StringSelect
          value={stringValue}
          onValueChange={(v) => onChange(v)}
          options={stringOptions}
          placeholder={t("Select an option")}
          className="w-full"
        />
      );
    }

    case "Multiple Select": {
      const selected = toStringArray(value);
      return (
        <MultiSelect
          id={fieldId}
          value={selected}
          onValueChange={(v) => onChange(v)}
          options={stringOptions}
          placeholder={t("Select options")}
        />
      );
    }

    case "Multiple Text": {
      const selected = toStringArray(value);
      return (
        <MultiSelect
          id={fieldId}
          value={selected}
          onValueChange={(v) => onChange(v)}
          options={stringArrayToMultiSelectOptions(selected)}
          creator={{
            render: (input) => t('Add "{{value}}"', { value: input }),
            transform: (input) => input,
            onCreate: () => {},
          }}
          placeholder={t("Add entries")}
        />
      );
    }

    case "Checkbox": {
      const isChecked = typeof value === "string" && value.startsWith("checkbox:");
      return (
        <Checkbox
          id={fieldId}
          checked={isChecked}
          onCheckedChange={(checked) => {
            if (checked === true) {
              onChange(`checkbox:${userId ?? ""}:${new Date().toISOString()}`);
            } else {
              onChange("");
            }
          }}
        />
      );
    }

    case "File": {
      return <Input id={fieldId} value={stringValue} disabled placeholder={t("Edit files from the Files section")} />;
    }

    case "Database Select": {
      const dbValue = isDatabaseSelectValue(value) ? value : [];
      const columns = customField.info.columns ?? [];
      const [primaryLabel, ...secondaryLabels] = columns;
      return (
        <DatabaseSelectField
          id={fieldId}
          value={dbValue}
          onChange={(next) => onChange(next ?? [])}
          options={{
            databaseId: customField.info.databaseId,
            databaseLabel: primaryLabel,
            databaseLabels: secondaryLabels,
          }}
        />
      );
    }

    case "Table": {
      const tableValue: TableInputCell[][] = isTableValue(value) ? value : [];
      return (
        <InputTableField
          id={fieldId}
          value={tableValue}
          onChange={(next) => onChange(next ?? [])}
          options={{ tableChoices: customField.info.columns ?? [] }}
        />
      );
    }

    case "Auto Detect": {
      return <Input id={fieldId} value={stringValue} onChange={(e) => onChange(e.target.value)} />;
    }
  }
};
